// @flow

import * as React from 'react';
import type { FormProps } from 'redux-form';
import { useDispatch } from 'react-redux';

import { Form } from 'gandi.react-form';

import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base, Button, Help, Radio } from '@gandi/ui';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type { UnsubscriptionState } from 'src/types';

import { deleteNotificationSubcribers } from './modules';

type RouterProps = {|
  ...WithRouterProps<{|
    token: string,
  |}>,
|};

type InjectedProps = {|
  reduxFormProps: FormProps,
|};

type OwnProps = {|
  infos: UnsubscriptionState,
  params: RouterProps['params'],
  router: RouterProps['router'],
|};

export type Props = {|
  ...OwnProps,
  ...InjectedProps,
|};
function UnsubscriptionForm({
  infos,
  params: { token },
  reduxFormProps: { handleSubmit, submitFailed },
  router,
}: Props): React.Element<typeof Form> {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const [checked, setChecked] = React.useState<string>(infos.subscription_key);

  React.useEffect(() => {
    if (!checked && infos.subscription_key) {
      setChecked(infos.subscription_key);
    }
  }, [checked, infos.subscription_key]);

  React.useEffect(() => {
    if (submitFailed) {
      const message = __(
        'A temporary error occured while sending your confirmation.'
      );
      dispatch(notifyDanger(message));
    }
  }, [__, dispatch, submitFailed]);

  const handleChange = (evt: SyntheticInputEvent<>) => {
    setChecked(evt.target.value);
  };

  const handleForm = () => {
    dispatch(deleteNotificationSubcribers(token, checked))
      .then(() => router.replace(`/unsubscription/${token}/success`))
      .catch(err => {
        const message =
          err.response.data.message ||
          __('A temporary error occured while sending your confirmation.');
        dispatch(notifyDanger(message));
      });
  };

  return (
    <Form method="post" onSubmit={handleSubmit(handleForm)}>
      <Base backgroundColor="white" padding="small" marginBottom="base">
        <Base tagName="h2">{__('I want to stop receiving:')}</Base>
        <Radio
          id={`radio-${infos.subscription_key}`}
          name="unsubscription_choice"
          value={infos.subscription_key}
          checked={checked === infos.subscription_key}
          onChange={handleChange}
        >
          <Base tagName="p" marginTop="none" marginBottom="small">
            {__('%(type)s notifications', {
              type: infos.label || infos.subscription_key,
            })}
          </Base>
        </Radio>

        <Radio
          id="radio-all"
          name="unsubscription_choice"
          value="all"
          checked={checked === 'all'}
          onChange={handleChange}
        >
          <Base tagName="p" marginY="none">
            {__('all notifications pertaining to %(orgaName)s', {
              orgaName: infos.display_name,
            })}
          </Base>
        </Radio>
        <Help
          variant="radio"
          dangerouslySetInnerHTML={{
            __html: __(
              'This option covers all emails except those for which you are personally legally responsible . You can subscribe back to each email notification individually afterwards if you want.'
            ),
          }}
        />
      </Base>
      <Base marginBottom="base">
        <Button type="submit">{__('Confirm')}</Button>
      </Base>
    </Form>
  );
}

export default UnsubscriptionForm;
